import {AbstractControl, ValidatorFn} from '@angular/forms';
import * as moment from 'moment';
import {GtPhoneNumberUtil} from '../utils/gt-phone-number.util';

const hourRegEx = /^$|^([0]?([01]?[0-9]|2[0-3]))$/;
const minuteRegEx = /^$|^[0]?([0-9]|[0-5][0-9])$/;

export function hourValidator(): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    return hourRegEx.test(control.value) ? null : {hour: {value: control.value}};
  };
}

export function minuteValidator(): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    return minuteRegEx.test(control.value) ? null : {minute: {value: control.value}};
  };
}

export function regExpValidator(allowedExp: RegExp, errorMessage?: string): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    if (control.value === null || control.value === undefined) {
      return null;
    }
    const allowed = allowedExp.test(control.value);
    return allowed
      ? null
      : {
          forbiddenCharacter: {value: control.value},
          message: errorMessage
        };
  };
}

/* Accepts only numbers */
export function numericValidator(): ValidatorFn {
  return regExpValidator(/^\d*$/, 'validator.numeric.error');
}

/* Accpets only alphabetical characters (Latin) including underscore, hyphen and space. No space at beginning or end. */
export function alphabeticValidator(): ValidatorFn {
  return regExpValidator(/^[^\s\d\W]([^\d\W]|[\u00C0-\u02B8\- ])*[^\s]$/, 'validator.alphabetic.error');
}

/* Accepts only numbers and alphabetical characters (Latin) including underscore, hyphen and space. No space at beginning or end. */
export function alphanumericValidator(): ValidatorFn {
  return regExpValidator(/^[^\s\W][\w\u00C0-\u02B8\- ]*[^\s]$/, 'validator.alphanumeric.error');
}

export function phoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    if (control.value === null || control.value === undefined) {
      return null;
    }
    return GtPhoneNumberUtil.isValidNumber(control.value)
      ? null
      : {
          forbiddenPhoneNumber: {value: control.value},
          message: 'validator.phone.error'
        };
  };
}

/* Validates that the input does NOT exist in the suplied array */
export function notExistsInArrayValidator<T>(arr?: Array<T>, errorMessage?: string): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    if (control.value === null || control.value === undefined || !arr) {
      return null;
    }
    return arr.some(val => control.value === val)
      ? {
          NotExistsInArray: {value: control.value},
          message: errorMessage ? errorMessage : 'validator.not_exists.generic_error'
        }
      : null;
  };
}

export const defaultCharacterLengthLimit = 110;

export function maxCharactersValidator(numberOfChar = defaultCharacterLengthLimit, errorMessage?: string): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    if (control.value === null || control.value === undefined || control.value === '') {
      return null;
    }
    return control.value.length > numberOfChar
      ? {
          tooManyCharacters: {value: control.value.length},
          message: errorMessage ? errorMessage : 'validator.110_characters.error'
        }
      : null;
  };
}

// If this validator is set as a form group validator it will validate that the first date is before the second date
export function chronologicDateValidator(startDateName: string, endDateName: string): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    if (!control.value || !control.get(startDateName) || !control.get(endDateName)) {
      return null;
    }
    const error = {
      dateChronologicError: {value: control.value},
      message: 'validator.chronolgiDate.error'
    };
    const firstControl = control.get(startDateName);
    const secondControl = control.get(endDateName);
    let first = firstControl.value;
    let second = secondControl.value;
    if (typeof first === 'string') {
      first = moment(first);
    }
    if (typeof second === 'string') {
      second = moment(second);
    }
    first = first as moment.Moment;
    second = second as moment.Moment;
    if (first.isAfter(second, 'd')) {
      firstControl.setErrors(error);
      secondControl.setErrors(error);
      firstControl.markAsTouched();
      secondControl.markAsTouched();
      return error;
    }
    firstControl.setErrors(null);
    secondControl.setErrors(null);
    return null;
  };
}

// Checks if a form controls value has a valid date using moment
export function validDate(): ValidatorFn {
  return (control: AbstractControl): {[key: string]: any} | null => {
    let date = control.value;
    if (typeof date === 'string') {
      date = moment(date);
    }
    return date.isValid()
      ? null
      : {
          dateInvalidError: {value: control.value},
          message: 'validator.validDate.error'
        };
  };
}
